import { Add, AppRegistration, SearchOutlined } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  TablePagination,
  TextField,
  Typography
} from '@mui/material'
import { ExplanationAccordion } from 'components'
import { ChipStatusSelect } from 'components/chips'
import { useAuth, useLayout } from 'context'
import * as controllers from 'controllers'
import { CompanyDto, StatusCount } from 'dtos'
import { useQuery } from 'hooks'
import { enqueueSnackbar } from 'notistack'
import { CompaniesParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getStatusCount, useDebounce } from 'utils'
import CompanyHeroCard from './CompanyHeroCard'
import CompanyAddEditDialog from './id/CompanyAddEditDialog'

const sessionStorageKey = 'companies:parameters'

export default function Companies() {
  const { permissions } = useAuth()
  const query = useQuery()
  const navigate = useNavigate()
  const layout = useLayout()

  const defaultParameters: CompaniesParameters = {
    page: 0,
    pageSize: 10,
    search: query.get('search') ?? '',
    statuses: ['isActive'],
    orderBy: 'name',
    order: 'asc'
  }

  const [companies, setCompanies] = useState<CompanyDto[]>([])
  const [company, setCompany] = useState<CompanyDto>(new CompanyDto())
  const [companyAddEditDialogOpen, setCompanyAddEditDialogOpen] = useState<boolean>(false)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isCreatingCompany, setIsCreatingCompany] = useState<boolean>(false)
  const [isGettingCompanies, setIsGettingCompanies] = useState<boolean>(true)
  const [parameters, setParameters] = useState<CompaniesParameters>(
    sessionStorage.getItem(sessionStorageKey) ? JSON.parse(sessionStorage.getItem(sessionStorageKey)!) : defaultParameters
  )
  const [statusCounts, setStatusCounts] = useState<StatusCount[]>([])

  const getCompanies = useDebounce((parameters: CompaniesParameters) => {
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(parameters))

    setIsGettingCompanies(true)
    controllers
      .getCompanies(parameters)
      .then(response => {
        setCompanies(response.value)
        setStatusCounts(response.statusCounts ?? [])
        setTotalCount(response.totalCount!)
      })
      .finally(() => {
        setIsGettingCompanies(false)
      })
  }, 300)

  useEffect(() => {
    getCompanies(parameters)
  }, [getCompanies, parameters])

  useEffect(() => {
    layout.setTitle('Companies')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(isGettingCompanies)
  }, [layout, isGettingCompanies])

  return (
    <>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <CompanyAddEditDialog
          initialValues={company}
          loading={isCreatingCompany}
          onClose={() => {
            setCompanyAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsCreatingCompany(true)
            controllers
              .createCompany(values)
              .then(_ => {
                enqueueSnackbar('Company Created Successfully!', { variant: 'success' })
                getCompanies(parameters)
                setCompanyAddEditDialogOpen(false)
              })
              .finally(() => {
                setIsCreatingCompany(false)
              })
          }}
          open={companyAddEditDialogOpen}
        />
      )}

      <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography fontWeight='bold' variant='h5'>
                Companies ({totalCount!})
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
                <Grid item xs={12} sm={true} container spacing={1}>
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      data-search
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <SearchOutlined fontSize='small' />{' '}
                          </InputAdornment>
                        )
                      }}
                      label='Search'
                      onChange={e => {
                        setParameters({ ...parameters, search: e.target.value ?? undefined })
                      }}
                      size='small'
                      sx={{ minWidth: '200px', maxWidth: '200px' }}
                      type='search'
                      value={parameters.search || ''}
                    />
                  </Grid>

                  <Grid item xs={12} sm='auto'>
                    <ChipStatusSelect
                      label='Status / Role'
                      onChange={value => {
                        setParameters({
                          ...parameters,
                          statuses: value
                        })
                      }}
                      options={[
                        {
                          label: `Active (${getStatusCount(statusCounts, 'isActive')})`,
                          color: 'green',
                          value: 'isActive'
                        },
                        {
                          label: `Inactive (${getStatusCount(statusCounts, 'isInactive')})`,
                          color: 'gray',
                          value: 'isInactive'
                        },
                        {
                          label: `Attorney Company (${getStatusCount(statusCounts, 'isAttorneyCompany')})`,
                          color: 'blue',
                          value: 'isAttorneyCompany'
                        },
                        {
                          label: `Broker (${getStatusCount(statusCounts, 'isBrokerCompany')})`,
                          color: 'yellow',
                          value: 'isBrokerCompany'
                        },
                        {
                          label: `Buyer Company (${getStatusCount(statusCounts, 'isBuyerCompany')})`,
                          color: 'purple',
                          value: 'isBuyerCompany'
                        },
                        {
                          label: `Exporter Company (${getStatusCount(statusCounts, 'isExporterCompany')})`,
                          color: 'orange',
                          value: 'isExporterCompany'
                        },
                        {
                          label: `Cluster Conversion Company (${getStatusCount(statusCounts, 'isClusterConversionCompany')})`,
                          color: 'pink',
                          value: 'isClusterConversionCompany'
                        },
                        {
                          label: `Cluster Programmer Company (${getStatusCount(statusCounts, 'isClusterProgrammerCompany')})`,
                          color: 'teal',
                          value: 'isClusterProgrammerCompany'
                        },
                        {
                          label: `Inbound Transporter (${getStatusCount(statusCounts, 'isInboundTransporterCompany')})`,
                          color: 'brown',
                          value: 'isInboundTransporterCompany'
                        },
                        {
                          label: `Importer Dealer (${getStatusCount(statusCounts, 'isImporterDealerCompany')})`,
                          color: 'lime',
                          value: 'isImporterDealerCompany'
                        },
                        {
                          label: `Inspection Company (${getStatusCount(statusCounts, 'isInspectionCompany')})`,
                          color: 'indigo',
                          value: 'isInspectionCompany'
                        },
                        {
                          label: `Insurance Company (${getStatusCount(statusCounts, 'isInsuranceCompany')})`,
                          color: 'cyan',
                          value: 'isInsuranceCompany'
                        },
                        {
                          label: `Outbound Transporter (${getStatusCount(statusCounts, 'isOutboundTransporterCompany')})`,
                          color: 'amber',
                          value: 'isOutboundTransporterCompany'
                        },
                        {
                          label: `Registered Importer (${getStatusCount(statusCounts, 'isRegisteredImporterCompany')})`,
                          color: 'violet',
                          value: 'isRegisteredImporterCompany'
                        },
                        {
                          label: `Surety Company (${getStatusCount(statusCounts, 'isSuretyCompany')})`,
                          color: 'olive',
                          value: 'isSuretyCompany'
                        },
                        {
                          label: `Title Destination (${getStatusCount(statusCounts, 'isTitleDestinationCompany')})`,
                          color: 'navy',
                          value: 'isTitleDestinationCompany'
                        },
                        {
                          label: `Vehicle Destination (${getStatusCount(statusCounts, 'isVehicleDestinationCompany')})`,
                          color: 'magenta',
                          value: 'isVehicleDestinationCompany'
                        }
                      ]}
                      value={parameters.statuses ?? []}
                    />
                  </Grid>

                  <Grid item xs={12} sm='auto'>
                    <Button
                      color='secondary'
                      disabled={Boolean(JSON.stringify(parameters) === JSON.stringify(defaultParameters))}
                      onClick={() => {
                        setParameters(defaultParameters)
                      }}
                      variant='text'
                    >
                      RESET FILTERS
                    </Button>
                  </Grid>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                    <Button
                      color='primary'
                      endIcon={<Add />}
                      onClick={() => {
                        setCompany(new CompanyDto())
                        setCompanyAddEditDialogOpen(true)
                      }}
                      variant='text'
                    >
                      ADD NEW
                    </Button>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {companies.map(company => (
          <Grid item xs={12} key={company.companyGuid}>
            <CompanyHeroCard company={company}>
              <Grid item xs={12} sm='auto'>
                <Button
                  endIcon={<AppRegistration />}
                  onClick={() => {
                    navigate(`/companies/${company.companyGuid}`)
                  }}
                  variant='text'
                >
                  DETAILS
                </Button>
              </Grid>
            </CompanyHeroCard>
          </Grid>
        ))}

        <Grid item xs={12}>
          <ExplanationAccordion>
            || The total number of companies matching the current filter criteria is displayed next to Companies list title in
            parenthesis. || The cards are sorted by Name ascending by default. || Search searches companies by Name, Website,
            and Notes. || The Status / Role multiselect filters the list by the selected status(es) and role(s), where if a
            company has one or more of the selected status(es) or role(s), it is displayed. || The total number of companies for
            a status / role is displayed next to the status' or role's name in parenthesis. This helps prevent users from adding
            a company that already exists, but is not displayed due to the current filter criteria. ||
          </ExplanationAccordion>
        </Grid>

        <Grid item xs={12}>
          <TablePagination
            component='div'
            count={totalCount}
            onPageChange={(_, page: number) => {
              setParameters({
                ...parameters,
                page
              })
            }}
            onRowsPerPageChange={e => {
              setParameters({
                ...parameters,
                page: 0,
                pageSize: e.target.value as unknown as number
              })
            }}
            page={parameters.page}
            rowsPerPage={parameters.pageSize}
            labelRowsPerPage={
              <Grid container spacing={1}>
                <Grid item>Order By:</Grid>

                <Grid item>
                  <TextField
                    InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                    onChange={e => {
                      setParameters({ ...parameters, orderBy: e.target.value as typeof parameters.orderBy })
                    }}
                    select
                    size='small'
                    value={parameters.orderBy}
                    variant='standard'
                  >
                    <MenuItem value='name'>Name</MenuItem>

                    <MenuItem value='website'>Website</MenuItem>

                    <MenuItem value='notes'>Notes</MenuItem>
                  </TextField>
                </Grid>

                <Grid item>
                  <TextField
                    InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                    onChange={e => {
                      setParameters({ ...parameters, order: e.target.value as 'asc' | 'desc' })
                    }}
                    select
                    size='small'
                    value={parameters.order}
                    variant='standard'
                  >
                    <MenuItem value='asc'>A-Z</MenuItem>

                    <MenuItem value='desc'>Z-A</MenuItem>
                  </TextField>
                </Grid>

                <Grid item sx={{ alignSelf: 'stretch' }}>
                  <Divider orientation='vertical' />
                </Grid>

                <Grid item>Cards Per Page:</Grid>
              </Grid>
            }
            rowsPerPageOptions={[
              { label: '5', value: 5 },
              { label: '10', value: 10 },
              { label: '25', value: 25 },
              { label: '50', value: 50 },
              { label: '100', value: 100 }
            ]}
            SelectProps={{ fullWidth: false }}
          />
        </Grid>
      </Grid>
    </>
  )
}
